import React, { useState, useEffect, useCallback } from 'react';
import {
  MdSpeed,
  MdRefresh,
  MdWarning,
  MdCheckCircle,
  MdInfo,
} from 'react-icons/md';
import { useClass } from '../utils/ClassContext';

const StudentQuotaPage = ({ username, userRole, apiBaseUrl }) => {
  const { selectedClass } = useClass();
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQuota = useCallback(async () => {
    if (!selectedClass) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${apiBaseUrl}/quota/my-environments?class_id=${selectedClass.class_id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'User-Role': userRole,
            'Requesting-User': username,
          },
          credentials: 'include',
        }
      );
      const data = await res.json();
      if (data.success) {
        setEnvironments(data.environments || []);
      } else {
        setError(data.error || 'Failed to load quota');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, userRole, username, selectedClass]);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const getStatus = (env) => {
    if (env.limit === -1) return 'unlimited';
    if (env.remaining <= 0) return 'exhausted';
    if (env.remaining <= 2) return 'low';
    const pct = env.used / env.limit;
    if (pct >= 0.8) return 'warning';
    return 'ok';
  };

  const statusConfig = {
    unlimited: {
      bar: 'bg-blue-500',
      badge: 'bg-blue-100 text-blue-800',
      label: 'Unlimited',
      icon: <MdInfo className="text-blue-500 text-xl" />,
    },
    ok: {
      bar: 'bg-green-500',
      badge: 'bg-green-100 text-green-800',
      label: 'Good',
      icon: <MdCheckCircle className="text-green-500 text-xl" />,
    },
    warning: {
      bar: 'bg-yellow-500',
      badge: 'bg-yellow-100 text-yellow-800',
      label: 'Running low',
      icon: <MdWarning className="text-yellow-500 text-xl" />,
    },
    low: {
      bar: 'bg-orange-500',
      badge: 'bg-orange-100 text-orange-800',
      label: 'Very low',
      icon: <MdWarning className="text-orange-500 text-xl" />,
    },
    exhausted: {
      bar: 'bg-red-500',
      badge: 'bg-red-100 text-red-800',
      label: 'Exhausted',
      icon: <MdWarning className="text-red-500 text-xl" />,
    },
  };

  if (!selectedClass) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6 text-center py-12">
          <MdSpeed className="text-6xl text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Please select a class to view your quota.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-1">My Feedback Quota</h1>
            <p className="text-gray-500 text-sm">
              {selectedClass.class_name} ({selectedClass.class_code}) — remaining AI feedback requests per environment
            </p>
          </div>
          <button
            onClick={fetchQuota}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <MdRefresh className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading your quota…</p>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <MdWarning className="text-red-500 text-2xl flex-shrink-0" />
            <div>
              <p className="text-red-800 font-medium">Error loading quota</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
          <button
            onClick={fetchQuota}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : environments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <MdSpeed className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No environments available yet.</p>
          <p className="text-gray-400 text-sm mt-1">Environments will appear here once your instructor publishes them.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {environments.map((env) => {
            const status = getStatus(env);
            const cfg = statusConfig[status];
            const pct = env.limit === -1 ? 100 : Math.min(100, (env.used / env.limit) * 100);

            return (
              <div
                key={env.environment_id}
                className={`bg-white rounded-lg shadow-lg p-6 border-l-4 ${
                  status === 'exhausted' ? 'border-red-500' :
                  status === 'low'       ? 'border-orange-500' :
                  status === 'warning'   ? 'border-yellow-500' :
                  status === 'unlimited' ? 'border-blue-500' :
                                          'border-green-500'
                }`}
              >
                {/* Environment name + status icon */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800 break-words">{env.name}</h2>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${cfg.badge}`}>
                      {cfg.label}
                    </span>
                  </div>
                  <div className="flex-shrink-0 ml-2">{cfg.icon}</div>
                </div>

                {/* Stats */}
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Used: <strong className="text-gray-800">{env.used}</strong></span>
                  <span>
                    Limit:{' '}
                    <strong className="text-gray-800">
                      {env.limit === -1 ? '∞' : env.limit}
                    </strong>
                  </span>
                  <span>
                    Remaining:{' '}
                    <strong className={
                      status === 'exhausted' ? 'text-red-700' :
                      status === 'low'       ? 'text-orange-700' :
                      status === 'warning'   ? 'text-yellow-700' :
                      status === 'unlimited' ? 'text-blue-700' :
                                              'text-green-700'
                    }>
                      {env.remaining === -1 ? '∞' : env.remaining}
                    </strong>
                  </span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${cfg.bar}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>

                {status === 'exhausted' && (
                  <p className="text-xs text-red-600 mt-2">
                    You have used all your feedback requests for this environment. Contact your instructor for more.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentQuotaPage;
